import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import type { PatientFormValues } from "./schema";

/** The patient form auto-saves a draft on this cadence (V2-E10.1, HAN-72). */
export const AUTOSAVE_INTERVAL_MS = 60_000;

export type DraftStatus = "idle" | "saving" | "saved";

export interface PatientFormDraftState {
  status: DraftStatus;
  lastSavedAt: Date | null;
  /** Save now if the form has changed since the last save — wired to form blur. */
  flush: () => void;
}

interface UsePatientFormDraftOptions {
  /** Off for a read-only (locked/acknowledged) form — nothing is auto-saved. */
  enabled: boolean;
  /** Persists the snapshot; resolves `true` when it saved. Best-effort. */
  onSave: (values: PatientFormValues) => Promise<boolean>;
}

/**
 * Auto-saves the patient form as a draft every 60s and on blur (HAN-72).
 *
 * Saving is decoupled from the real submit: it ignores the SBAR/priority Save
 * gating entirely (a draft is allowed to be incomplete) and never surfaces an
 * error — a failed save just leaves the indicator and retries on the next tick.
 * A JSON snapshot of the last-saved values short-circuits redundant writes, so an
 * idle form (or repeated blurs with no edits) never hits the network.
 */
export function usePatientFormDraft(
  form: UseFormReturn<PatientFormValues>,
  { enabled, onSave }: UsePatientFormDraftOptions,
): PatientFormDraftState {
  const [status, setStatus] = useState<DraftStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Seeded from the initial values so an untouched form (incl. one restored from
  // a draft) stays idle until the doctor actually changes something.
  const lastSnapshotRef = useRef<string>(JSON.stringify(form.getValues()));
  const inFlightRef = useRef(false);
  // Keep the latest onSave without re-arming the interval each render.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const saveIfChanged = useCallback(async (): Promise<void> => {
    if (!enabled || inFlightRef.current) return;
    const values = form.getValues();
    const snapshot = JSON.stringify(values);
    if (snapshot === lastSnapshotRef.current) return;

    inFlightRef.current = true;
    setStatus("saving");
    let ok = false;
    try {
      ok = await onSaveRef.current(values);
    } finally {
      inFlightRef.current = false;
    }

    if (ok) {
      lastSnapshotRef.current = snapshot;
      setStatus("saved");
      setLastSavedAt(new Date());
    } else {
      // Leave the snapshot stale so the next tick/blur retries. Don't claim a
      // save: fall back to "saved" if we ever had one, else "idle".
      setStatus((prev) => (prev === "saving" ? "idle" : prev));
    }
  }, [enabled, form]);

  // 60s heartbeat.
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => void saveIfChanged(), AUTOSAVE_INTERVAL_MS);
    return () => {
      clearInterval(id);
    };
  }, [enabled, saveIfChanged]);

  const flush = useCallback((): void => {
    void saveIfChanged();
  }, [saveIfChanged]);

  return { status, lastSavedAt, flush };
}
